import React, { useState, useEffect, useRef } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label"; // Use Label for standalone inputs

const StyleGuide = () => {
  const ColorSwatch = ({
    bgColorClass,
    textColorClass,
    name,
    cssVarName,
  }: {
    bgColorClass: string;
    textColorClass: string;
    name: string;
    cssVarName: string; // Add CSS variable name prop
  }) => {
    const [colorValue, setColorValue] = useState("");
    // No longer need swatchRef for background
    // const swatchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      // Read the CSS variable value from the root element
      const computedValue = getComputedStyle(document.documentElement)
        .getPropertyValue(cssVarName)
        .trim();
      setColorValue(computedValue || "N/A");
    }, [cssVarName]); // Rerun if variable name changes

    // Define swatchStyle with a flexible type initially
    const swatchStyle: React.CSSProperties = {
      backgroundColor:
        colorValue !== "N/A" ? `hsl(${colorValue})` : "transparent",
    };

    // Special handling for border color
    if (name === "Border") {
      swatchStyle.backgroundColor = "transparent";
      swatchStyle.borderColor =
        colorValue !== "N/A" ? `hsl(${colorValue})` : "#ccc";
      swatchStyle.borderWidth = "2px";
      swatchStyle.borderStyle = "solid"; // Add border style for visibility
    }

    return (
      <div className="flex items-center gap-4 p-4 border rounded-md">
        {/* Apply color using inline style */}
        <div
          className={`h-10 w-10 rounded border border-gray-200`} // Basic structure + border
          style={swatchStyle}
        ></div>
        <div>
          <div className="font-medium">{name}</div>
          {/* Display the CSS variable value and class names */}
          <div
            className={`text-sm ${textColorClass} ${bgColorClass === "bg-background" ? "text-foreground" : ""}`}
          >
            {colorValue} ({bgColorClass} / {textColorClass})
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-8 space-y-12">
        <h1 className="text-3xl font-bold mb-4">Style Guide</h1>

        {/* Colors Section - Pass CSS Variable Names */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">
            System Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorSwatch
              cssVarName="--background"
              bgColorClass="bg-background"
              textColorClass="text-foreground"
              name="Background"
            />
            <ColorSwatch
              cssVarName="--foreground"
              bgColorClass="bg-foreground"
              textColorClass="text-background"
              name="Foreground"
            />
            <ColorSwatch
              cssVarName="--primary"
              bgColorClass="bg-primary"
              textColorClass="text-primary-foreground"
              name="Primary"
            />
            <ColorSwatch
              cssVarName="--primary-foreground"
              bgColorClass="bg-primary-foreground"
              textColorClass="text-primary"
              name="Primary Foreground"
            />
            <ColorSwatch
              cssVarName="--secondary"
              bgColorClass="bg-secondary"
              textColorClass="text-secondary-foreground"
              name="Secondary"
            />
            <ColorSwatch
              cssVarName="--secondary-foreground"
              bgColorClass="bg-secondary-foreground"
              textColorClass="text-secondary"
              name="Secondary Foreground"
            />
            <ColorSwatch
              cssVarName="--muted"
              bgColorClass="bg-muted"
              textColorClass="text-muted-foreground"
              name="Muted"
            />
            <ColorSwatch
              cssVarName="--muted-foreground"
              bgColorClass="bg-muted-foreground"
              textColorClass="text-muted"
              name="Muted Foreground"
            />
            <ColorSwatch
              cssVarName="--accent"
              bgColorClass="bg-accent"
              textColorClass="text-accent-foreground"
              name="Accent"
            />
            <ColorSwatch
              cssVarName="--accent-foreground"
              bgColorClass="bg-accent-foreground"
              textColorClass="text-accent"
              name="Accent Foreground"
            />
            <ColorSwatch
              cssVarName="--destructive"
              bgColorClass="bg-destructive"
              textColorClass="text-destructive-foreground"
              name="Destructive"
            />
            <ColorSwatch
              cssVarName="--destructive-foreground"
              bgColorClass="bg-destructive-foreground"
              textColorClass="text-destructive"
              name="Destructive Foreground"
            />
            <ColorSwatch
              cssVarName="--card"
              bgColorClass="bg-card"
              textColorClass="text-card-foreground"
              name="Card"
            />
            <ColorSwatch
              cssVarName="--card-foreground"
              bgColorClass="bg-card-foreground"
              textColorClass="text-card"
              name="Card Foreground"
            />
            <ColorSwatch
              cssVarName="--popover"
              bgColorClass="bg-popover"
              textColorClass="text-popover-foreground"
              name="Popover"
            />
            <ColorSwatch
              cssVarName="--popover-foreground"
              bgColorClass="bg-popover-foreground"
              textColorClass="text-popover"
              name="Popover Foreground"
            />
            <ColorSwatch
              cssVarName="--border"
              bgColorClass="border"
              textColorClass="text-foreground"
              name="Border"
            />
            <ColorSwatch
              cssVarName="--input"
              bgColorClass="bg-input"
              textColorClass="text-foreground"
              name="Input"
            />
            <ColorSwatch
              cssVarName="--ring"
              bgColorClass="bg-ring"
              textColorClass="text-primary-foreground"
              name="Ring"
            />
          </div>
        </section>

        {/* Official Brand Colors Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">
            Official Brand Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ColorSwatch
              cssVarName="--brand-yellow"
              bgColorClass="bg-yellow-400"
              textColorClass="text-yellow-900"
              name="Brand Yellow"
            />
            <ColorSwatch
              cssVarName="--brand-blue"
              bgColorClass="bg-blue-300"
              textColorClass="text-blue-900"
              name="Brand Light Blue"
            />
            <ColorSwatch
              cssVarName="--brand-green"
              bgColorClass="bg-green-600"
              textColorClass="text-green-50"
              name="Brand Green"
            />
            <ColorSwatch
              cssVarName="--brand-purple"
              bgColorClass="bg-purple-500"
              textColorClass="text-purple-50"
              name="Brand Purple"
            />
            <ColorSwatch
              cssVarName="--brand-red"
              bgColorClass="bg-red-500"
              textColorClass="text-red-50"
              name="Brand Red"
            />
          </div>
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">Usage Guidelines:</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>
                <strong>Yellow (#eab308):</strong> Primary actions, highlights, 
                attention-drawing elements
              </li>
              <li>
                <strong>Blue (#9eacd3):</strong> Information, calm states,
                professional tone (now used for ring/focus states)
              </li>
              <li>
                <strong>Green (#059669):</strong> Success states, positive
                actions, confirmations
              </li>
              <li>
                <strong>Purple (#6366f1):</strong> Premium features, creative
                elements, special content
              </li>
              <li>
                <strong>Red (#ef4444):</strong> Errors, alerts, destructive
                actions
              </li>
            </ul>
          </div>
        </section>

        {/* Brand Color Usage Examples */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">
            Brand Color Usage Examples
          </h2>
          <div className="space-y-6">
            {/* Tailwind Utility Classes */}
            <div>
              <h3 className="text-lg font-medium mb-3">
                Tailwind Utility Classes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Button className="bg-brand-yellow text-black hover:bg-yellow-400">
                  Yellow Button
                </Button>
                <Button className="bg-brand-blue text-white hover:bg-blue-400">
                  Blue Button
                </Button>
                <Button className="bg-brand-green text-white hover:bg-green-700">
                  Green Button
                </Button>
                <Button className="bg-brand-purple text-white hover:bg-purple-600">
                  Purple Button
                </Button>
                <Button className="bg-brand-red text-white hover:bg-red-600">
                  Red Button
                </Button>
              </div>
            </div>

            {/* Semantic Usage */}
            <div>
              <h3 className="text-lg font-medium mb-3">Semantic Usage</h3>
              <div className="space-y-2">
                <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
                  <p className="text-success font-medium">
                    ✅ Success: Operation completed successfully!
                  </p>
                </div>
                <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <p className="text-warning font-medium">
                    ⚠️ Warning: Please review this information.
                  </p>
                </div>
                <div className="p-3 bg-info/10 border border-info/20 rounded-lg">
                  <p className="text-info font-medium">
                    ℹ️ Info: Here's some helpful information.
                  </p>
                </div>
              </div>
            </div>

            {/* CSS Variable Usage */}
            <div>
              <h3 className="text-lg font-medium mb-3">CSS Variable Usage</h3>
              <div className="bg-muted p-4 rounded-lg">
                <pre className="text-sm">
                  {`/* In your CSS/Tailwind */
.custom-element {
  background-color: hsl(var(--brand-yellow));
  color: hsl(var(--brand-green));
}

/* Tailwind utilities */
className="bg-brand-yellow text-brand-green"`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Typography Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">
            Typography
          </h2>
          <div className="space-y-4">
            <h1 className="text-4xl font-bold">
              H1 Heading - The quick brown fox
            </h1>
            <h2 className="text-3xl font-semibold">
              H2 Heading - The quick brown fox
            </h2>
            <h3 className="text-2xl font-semibold">
              H3 Heading - The quick brown fox
            </h3>
            <h4 className="text-xl font-medium">
              H4 Heading - The quick brown fox
            </h4>
            <h5 className="text-lg font-medium">
              H5 Heading - The quick brown fox
            </h5>
            <h6 className="text-base font-medium">
              H6 Heading - The quick brown fox
            </h6>
            <p>
              Paragraph - The quick brown fox jumps over the lazy dog. Lorem
              ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod
              tempor incididunt ut labore et dolore magna aliqua.
            </p>
            <p className="text-lg">
              Large Paragraph - The quick brown fox jumps over the lazy dog.
            </p>
            <p className="text-sm">
              Small Paragraph - The quick brown fox jumps over the lazy dog.
            </p>
            <p className="text-muted-foreground">
              Muted Paragraph - The quick brown fox jumps over the lazy dog.
            </p>
            <blockquote>
              Blockquote - The quick brown fox jumps over the lazy dog.
            </blockquote>
            <a href="#" className="text-primary hover:underline">
              Link Example
            </a>
          </div>
        </section>

        {/* Buttons Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">Buttons</h2>
          <div className="flex flex-wrap gap-4 items-center">
            <Button>Default (Primary)</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
            <Button disabled>Disabled</Button>
          </div>
        </section>

        {/* Forms Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">
            Form Elements
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="input-example">Input</Label>
                <Input id="input-example" placeholder="Placeholder text" />
              </div>
              <div>
                <Label htmlFor="input-disabled">Input (Disabled)</Label>
                <Input id="input-disabled" placeholder="Disabled" disabled />
              </div>
              <div>
                <Label htmlFor="textarea-example">Textarea</Label>
                <Textarea
                  id="textarea-example"
                  placeholder="Type something here..."
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="select-example">Select</Label>
                <Select>
                  <SelectTrigger id="select-example">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="option1">Option 1</SelectItem>
                    <SelectItem value="option2">Option 2</SelectItem>
                    <SelectItem value="option3">Option 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 pt-4">
                <Checkbox id="checkbox-example" />
                <Label htmlFor="checkbox-example">Checkbox Example</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="checkbox-disabled" disabled />
                <Label htmlFor="checkbox-disabled">Checkbox (Disabled)</Label>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 border-b pb-2">Tabs</h2>
          <Tabs defaultValue="tab1">
            <TabsList>
              <TabsTrigger value="tab1">Tab One</TabsTrigger>
              <TabsTrigger value="tab2">Tab Two</TabsTrigger>
              <TabsTrigger value="tab3" disabled>
                Tab Three (Disabled)
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tab1">
              <Card>
                <CardContent className="p-6">
                  Content for Tab One goes here.
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="tab2">
              <Card>
                <CardContent className="p-6">
                  Content for Tab Two goes here. Different content.
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="tab3">
              <Card>
                <CardContent className="p-6">
                  Content for Tab Three.
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </section>
      </div>
    </MainLayout>
  );
};

export default StyleGuide;
